import { formatCurrency } from '../../lib/CurrencyHelpers.js';
import { formatDateTime, formatDateTimeLong } from '../../lib/DateTime.js';
import {
    CheckCircleIcon,
    ExclamationCircleIcon,
    InformationCircleIcon,
    XCircleIcon,
} from '@heroicons/react/24/outline';
import { sumProperty, ucfirst } from '../../lib/Utils.js';
import { useState } from 'react';
import Modal from '../Modal';
import EditManualPayment from './EditManualPayment.jsx';
import CertificateIcon from '~/components/icons/CertificateIcon';
import { couponHumanDiscount } from '~/lib/CouponHelpers';
import GiftCertificatePaymentLine from '~/components/giftcertificates/GiftCertificatePaymentLine';
import useBusinessPostApi from '~/lib/useBusinessPostApi';

const ItemRow = ({ icon, title, description, amount, isFirst }) => {
    return (
        <div className="flex">
            <div className="w-6 h-6 mr-2 py-3">{icon}</div>
            <div
                className={`flex py-3 pr-4 justify-between items-center flex-1 ${!isFirst ? 'border-t' : ''}`}
            >
                <div>
                    {title}
                    <div className="text-sm text-gray-500">{description}</div>
                </div>
                {!isNaN(parseFloat(amount)) && (
                    <div className="font-medium ml-4">
                        {formatCurrency(amount)}
                    </div>
                )}
            </div>
        </div>
    );
};

const PaymentHistoryRow = ({
    payment,
    isFirst,
    isLast,
    onUpdate,
    renderDetails,
    refetch,
}) => {
    const processorName = ucfirst(payment.processor);
    const isFailed = !!payment?.is_failed;

    return (
        <ItemRow
            icon={
                isFailed ? (
                    <XCircleIcon className="text-red-600" />
                ) : (
                    <CheckCircleIcon className="text-green-500" />
                )
            }
            title={isFailed ? 'Payment Failed' : 'Payment'}
            isFirst={isFirst}
            amount={processorName === 'Skip' ? 0 : payment.amount}
            description={
                <div>
                    <div>{formatDateTimeLong(payment.created_at)}</div>
                    {!!payment.external_link && (
                        <div>
                            via{' '}
                            <a
                                href={payment.external_link}
                                target="_blank"
                                className="text-gray-500"
                            >
                                {processorName}
                            </a>
                        </div>
                    )}
                    {!payment.external_link && !isFailed && (
                        <div>
                            {processorName === 'Skip' &&
                                'Skipped paying online'}
                            {processorName === 'Giftcertificate' && (
                                <GiftCertificatePaymentLine payment={payment} />
                            )}
                            {processorName !== 'Skip' &&
                                processorName !== 'Giftcertificate' &&
                                `via ${processorName}`}
                            {!!payment.external_id &&
                                processorName !== 'Giftcertificate' &&
                                ` (${payment.external_id})`}
                            {payment.processor === 'manual' && (
                                <EditManualPayment
                                    payment={payment}
                                    onUpdate={onUpdate}
                                />
                            )}
                        </div>
                    )}
                    {!!payment.settings?.notes && (
                        <div className="mb-2">{payment.settings?.notes}</div>
                    )}

                    {!!renderDetails &&
                        renderDetails({
                            payment,
                            isFirst,
                            isLast,
                            refetchPayments: refetch,
                        })}
                </div>
            }
        />
    );
};

/**
 * @param label What this is showing payments for, ex., 'event'. Used when describing
 *  the total and the balance so it's clear those are only for this object.
 * @param extraItems Optional rows to show alongside the payments, ex., money that was paid
 *  but isn't recorded as a payment on this object. These are informational only, they are
 *  not included in the amount paid for this object.
 *  Each is {key, created_at, icon, title, description, amount}
 */
const PaymentHistory = ({
    totalPrice,
    history,
    codes,
    savedPaymentMethods,
    onUpdate,
    onRemoveCoupons,
    isRemovingCoupons,
    renderDetails,
    refetch,
    extraItems,
    label,
}) => {
    const objectLabel = label || 'item';
    const payments = Array.isArray(history) ? history : [];
    const totalPaid = sumProperty(payments, 'amount');
    const amountDue = Math.max(0, totalPrice - totalPaid);

    // Payments and any extra rows, in the order they happened
    const rows = [
        ...payments.map((payment, idx) => ({
            key: `payment-${payment.id}`,
            createdAt: payment.created_at,
            payment,
            isLastPayment: idx === payments.length - 1,
        })),
        ...(extraItems || []).map(item => ({
            key: item.key,
            createdAt: item.created_at,
            item,
        })),
    ].sort(
        (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

    return (
        <div className="panel pl-4">
            {rows.map((row, idx) =>
                row.payment ? (
                    <PaymentHistoryRow
                        payment={row.payment}
                        key={row.key}
                        isFirst={idx === 0}
                        isLast={row.isLastPayment}
                        onUpdate={onUpdate}
                        renderDetails={renderDetails}
                        refetch={refetch}
                    />
                ) : (
                    <ItemRow
                        key={row.key}
                        icon={row.item.icon}
                        title={row.item.title}
                        description={row.item.description}
                        amount={row.item.amount}
                        isFirst={idx === 0}
                    />
                ),
            )}
            {codes?.map(code => (
                <ItemRow
                    key={code?.id}
                    icon={<CertificateIcon className="text-[#D37DE8] mt-1" />}
                    title={code?.voucher?.name || 'Unknown Code'}
                    amount={null}
                    description={
                        <div>
                            Code {code?.code} (
                            {couponHumanDiscount(code?.voucher)} off)
                            {typeof onRemoveCoupons === 'function' && (
                                <>
                                    <span className="mx-1">&middot;</span>
                                    <button
                                        type="submit"
                                        className="link-no-underline"
                                        onClick={() => {
                                            onRemoveCoupons();
                                        }}
                                    >
                                        {isRemovingCoupons
                                            ? 'Removing...'
                                            : 'Remove'}
                                    </button>
                                </>
                            )}
                        </div>
                    }
                    isFirst={false}
                />
            ))}
            {!rows.length && !!savedPaymentMethods?.length && (
                <ItemRow
                    icon={<InformationCircleIcon className="text-green-500" />}
                    title="Card on File"
                    amount={null}
                    description={`A verified payment method has been saved for this ${objectLabel}`}
                    isFirst={!rows.length}
                />
            )}
            {amountDue > 0 && (
                <ItemRow
                    icon={<ExclamationCircleIcon className="text-red-600" />}
                    title="Balance Due"
                    amount={amountDue}
                    description={`Total for this ${objectLabel} is ${formatCurrency(totalPrice)}, but ${formatCurrency(totalPaid)} has been paid`}
                    isFirst={!rows.length}
                />
            )}
            {amountDue <= 0 && totalPaid > 0 && (
                <ItemRow
                    icon={<CheckCircleIcon className="text-green-500" />}
                    title="Fully Paid"
                    amount={null}
                    isFirst={!rows.length}
                    description={
                        totalPrice > 0
                            ? `Total for this ${objectLabel} is ${formatCurrency(totalPrice)}, amount paid is ${formatCurrency(totalPaid)}`
                            : `Total amount paid is ${formatCurrency(totalPaid)}`
                    }
                />
            )}
        </div>
    );
};

export default PaymentHistory;
